import { LoadingPage } from '@chiroup/components/LoadingPage';
import { MagnifyingGlassIcon, MapPinIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import useClinicMapLocations from '../../hooks/useClinicMapLocations';
import Header from '../layout/Header';
import ClinicMapView, { COLOR_SCHEMES, ColorByOption, MapStyle, ViewMode } from './ClinicMapView';
import FilterPanel from './FilterPanel';
import MapControls from './MapControls';
import MapLegend from './MapLegend';
import StatsPanel from './StatsPanel';
import useClinicMapFilters from './useClinicMapFilters';

const ClinicMap = () => {
  const { locations, total, isLoading, error, refetch } = useClinicMapLocations();

  // All filter state and logic is now in the custom hook
  const filters = useClinicMapFilters(locations);

  // UI state for panels
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('markers');
  const [mapStyle, setMapStyle] = useState<MapStyle>('street');

  // Merge custom colors with defaults
  const mergedColorSchemes = useMemo(() => {
    const merged: Record<ColorByOption, Record<string, string>> = {
      plan: { ...COLOR_SCHEMES.plan, ...filters.customColors.plan },
      status: { ...COLOR_SCHEMES.status, ...filters.customColors.status },
      schedule: { ...COLOR_SCHEMES.schedule, ...filters.customColors.schedule },
      postType: { ...COLOR_SCHEMES.postType, ...filters.customColors.postType },
      platform: { ...COLOR_SCHEMES.platform, ...filters.customColors.platform },
    };
    return merged;
  }, [filters.customColors]);

  if (isLoading) {
    return (
      <>
        <Header title="Clinics Map" />
        <LoadingPage />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header title="Clinics Map" />
        <div className="p-6">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-red-800">Failed to load clinic locations. Please try again.</p>
            <button
              onClick={() => refetch()}
              className="mt-2 rounded bg-red-100 px-4 py-2 text-red-800 hover:bg-red-200"
            >
              Retry
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header
        title={
          <div className="flex items-center gap-2">
            <MapPinIcon className="h-6 w-6 text-primary-500" />
            <span>Clinics Map</span>
          </div>
        }
        afterTitle={
          <span className="text-sm text-gray-500">
            Showing {filters.filteredLocations.length.toLocaleString()} of {total.toLocaleString()}{' '}
            clinics
          </span>
        }
      />

      <div className="relative h-[calc(100vh-140px)]">
        {/* Search and Filter Controls */}
        <div className="absolute right-4 top-4 z-[1000] flex items-center gap-2">
          {/* View Mode Toggle and Map Style Switcher */}
          <MapControls
            viewMode={viewMode}
            setViewMode={setViewMode}
            mapStyle={mapStyle}
            setMapStyle={setMapStyle}
          />

          {/* Search Bar */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search clinic, city, state..."
              value={filters.searchQuery}
              onChange={e => filters.setSearchQuery(e.target.value)}
              className="w-64 rounded-md border border-gray-300 bg-white py-2 pl-9 pr-8 text-sm shadow-md placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            {filters.searchQuery && (
              <button
                onClick={() => filters.setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Stats Panel */}
          <StatsPanel
            locations={locations}
            availableStates={filters.availableStates}
            isOpen={isStatsOpen}
            onClose={() => setIsStatsOpen(false)}
            onToggle={() => setIsStatsOpen(!isStatsOpen)}
          />

          {/* Filter Panel */}
          <FilterPanel
            locations={locations}
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            onToggle={() => setIsFilterOpen(!isFilterOpen)}
            activeFilterCount={filters.activeFilterCount}
            selectedPlans={filters.selectedPlans}
            setSelectedPlans={filters.setSelectedPlans}
            selectedStatus={filters.selectedStatus}
            setSelectedStatus={filters.setSelectedStatus}
            selectedSchedules={filters.selectedSchedules}
            setSelectedSchedules={filters.setSelectedSchedules}
            selectedPlatforms={filters.selectedPlatforms}
            setSelectedPlatforms={filters.setSelectedPlatforms}
            selectedPostTypes={filters.selectedPostTypes}
            setSelectedPostTypes={filters.setSelectedPostTypes}
            selectedStatesFilter={filters.selectedStatesFilter}
            setSelectedStatesFilter={filters.setSelectedStatesFilter}
            startDate={filters.startDate}
            setStartDate={filters.setStartDate}
            endDate={filters.endDate}
            setEndDate={filters.setEndDate}
            filterMode={filters.filterMode}
            setFilterMode={filters.setFilterMode}
            filterRelations={filters.filterRelations}
            setFilterRelations={filters.setFilterRelations}
            colorBy={filters.colorBy}
            setColorBy={filters.setColorBy}
            customColors={filters.customColors}
            onColorChange={filters.handleColorChange}
            filteredFilterStates={filters.filteredFilterStates}
            statesFilterSearch={filters.statesFilterSearch}
            setStatesFilterSearch={filters.setStatesFilterSearch}
          />
        </div>

        {/* Legend */}
        <MapLegend
          colorBy={filters.colorBy}
          viewMode={viewMode}
          colorSchemes={mergedColorSchemes}
        />

        <ClinicMapView
          locations={filters.filteredLocations}
          colorBy={filters.colorBy}
          customColorSchemes={mergedColorSchemes}
          viewMode={viewMode}
          mapStyle={mapStyle}
        />
      </div>
    </>
  );
};

export default ClinicMap;
